/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useState} from 'react'
import {
    Box,
    Heading,
    Flex,
    Link as ChakraLink,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {UnorderedList, ListItem} from '@chakra-ui/react'
import {Helmet} from 'react-helmet'
import {useIntl} from 'react-intl'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import Link from '@salesforce/retail-react-app/app/components/link'
import Search from '../../components/search'
import {
    CONTACT_PHONE_NUMBERS,
    CONTACT_US_URL,
    EINSTEIN_RECOMMENDERS,
    FAQ_URL,
    PAGE_NOT_FOUND_PDC_PAGE_ID
} from '../../constants'
import useMultiSite from '../../hooks/use-multi-site'
import {usePage} from '@salesforce/commerce-sdk-react'
import {
    PDCLink,
    PDCHomeServicesPushTile,
    PDCHomeCategoriesPushTile,
    PDCInnerAccordion,
    PDCBlogArticlesList
} from '../../page-designer/assets'
import {
    PDCHomeDyptich,
    PDCHomeFullScreenPushImage,
    PDCHomeFullScreenPushVideo,
    PDCHomeProductDyptichVideo,
    PDCHomeProductDyptichImage,
    PDCHomeServicesPush,
    PDCHomeCategoriesPush,
    PDCAboutTextBlock,
    PDCButton,
    PDCTitlePageOnHeaderImage,
    PDCAnchorsBlock,
    PDCAccordionBlock,
    PDCAccordion,
    PDCAboutStoryBlockDefine,
    PDCAboutStoryBlockExcerpt,
    PDCHomeHeroArticle,
    PDCClassicProductCarousel
} from '../../page-designer/layouts'
import {getPageDesignerRegion} from '../../utils/utils'
import {Component, PageContext} from '@salesforce/commerce-sdk-react/components'
import RecommendedProducts from '../../components/recommended-products'
import {useGTM} from '@app/hooks/use-gtm'
import useSiteType from '@app/hooks/use-site-type'

const PAGEDESIGNER_TO_COMPONENT = {
    'commerce_assets.pdcLink': PDCLink,
    'commerce_assets.pdcDisplayConfigLink': PDCLink,
    'commerce_assets.pdcHomeServicesPushTile': PDCHomeServicesPushTile,
    'commerce_assets.pdcHomeCategoriesPushTile': PDCHomeCategoriesPushTile,
    'commerce_assets.pdcInnerAccordion': PDCInnerAccordion,
    'commerce_assets.pdcBlogArticlesList': PDCBlogArticlesList,
    'commerce_layouts.pdcHomeDyptich': PDCHomeDyptich,
    'commerce_layouts.pdcHomeFullScreenPushImage': PDCHomeFullScreenPushImage,
    'commerce_layouts.pdcHomeFullScreenPushVideo': PDCHomeFullScreenPushVideo,
    'commerce_layouts.pdcHomeProductDyptichVideo': PDCHomeProductDyptichVideo,
    'commerce_layouts.pdcHomeProductDyptichImage': PDCHomeProductDyptichImage,
    'commerce_layouts.pdcHomeServicesPush': PDCHomeServicesPush,
    'commerce_layouts.pdcHomeCategoriesPush': PDCHomeCategoriesPush,
    'commerce_layouts.pdcAboutTextBlock': PDCAboutTextBlock,
    'commerce_layouts.pdcButton': PDCButton,
    'commerce_layouts.pdcTitlePageOnHeaderImage': PDCTitlePageOnHeaderImage,
    'commerce_layouts.pdcAnchorsBlock': PDCAnchorsBlock,
    'commerce_layouts.pdcAccordionBlock': PDCAccordionBlock,
    'commerce_layouts.pdcAccordion': PDCAccordion,
    'commerce_layouts.pdcAboutStoryBlockDefine': PDCAboutStoryBlockDefine,
    'commerce_layouts.pdcAboutStoryBlockExcerpt': PDCAboutStoryBlockExcerpt,
    'commerce_layouts.pdcHomeHeroArticle': PDCHomeHeroArticle,
    'commerce_layouts.pdcClassicProductCarousel': PDCClassicProductCarousel
}

/**
 * PageNotFound is a React functional component that renders a user-friendly 404 error page.
 * This component is designed to inform users that the requested page could not be found and
 * provides helpful navigation options to guide them back to relevant sections of the website.
 *
 * The component utilizes several hooks and context providers to fetch and display localized
 * content, including internationalized messages, site-specific phone numbers, and page designer
 * components. It also sets the HTTP response status to 404 if a server response object is available.
 *
 * Key Features:
 * - Displays a message indicating that the page could not be found.
 * - Provides a search bar for users to search for content within the site.
 * - Offers contact options, including a phone number and email link, tailored to the user's locale.
 * - Includes navigation links to FAQs and user account management.
 * - Renders additional components from a page designer configuration, if available.
 * - Showcases recommended products to engage users further.
 *
 * The component leverages Chakra UI for styling and layout, ensuring a responsive and visually
 * appealing design across different screen sizes.
 */
const PageNotFound = () => {
    const intl = useIntl()
    const {res} = useServerContext()
    const styles = useMultiStyleConfig('PageNotFound')
    const {site, locale} = useMultiSite()

    // Get page not found page designer components
    const {isOutlet} = useSiteType()
    const {data: pageNotFoundPage} = usePage({
        parameters: {pageId: PAGE_NOT_FOUND_PDC_PAGE_ID + (isOutlet ? '-outlet' : '')}
    })
    const pageNotFoundComponents = getPageDesignerRegion(pageNotFoundPage, 'main')?.components
    const [contextValue] = useState({components: PAGEDESIGNER_TO_COMPONENT})

    /**
     * Extracts and returns the last segment from a hyphen-separated site alias string.
     *
     * @param {string} alias - The site alias string, typically containing segments separated by hyphens.
     * @returns {string} The last segment of the alias after splitting by hyphens.
     */
    const getNormalizedSiteAlias = (alias) => {
        // Split the alias by hyphen and return the last segment
        const parts = alias.split('-')
        return parts[parts.length - 1]
    }
    // Get phone number according to the site's alias and locales.
    // Defaults to the `en` phone number if a locale-specific one is not found.
    const phoneNumbers = CONTACT_PHONE_NUMBERS
    const siteAlias = getNormalizedSiteAlias(site.alias)

    const tel = phoneNumbers[siteAlias][locale.alias] || phoneNumbers[siteAlias].en
    const {triggerErrorEvent} = useGTM()

    if (res) {
        res.status(404)
    }

    useEffect(() => {
        // GTM Integration
        triggerErrorEvent()
    }, [])
    useEffect(() => {
        window.__IS_404__ = true

        return () => {
            // When leaving 404 page, reset automatically
            window.__IS_404__ = false
        }
    }, [])

    return (
        <Box layerStyle="page" className="page-not-found" height={'100%'} pb={['64px', null, 120]}>
            <Helmet>
                <title>
                    {intl.formatMessage({
                        defaultMessage: "The page you're looking for can't be found.",
                        id: 'page_not_found.title.page_cant_be_found'
                    })}
                </title>
            </Helmet>

            <Flex
                justify="center"
                align="center"
                flexDirection="column"
                px={16}
                py={'64px'}
                gap={[40, null, '52px']}
            >
                <Heading as="h1" size={'4xl'} textAlign={['left', null, 'center']}>
                    {intl.formatMessage({
                        defaultMessage: 'SORRY, THE REQUESTED PAGE COULD NOT BE FOUND.',
                        id: 'page_not_found.title.page_cant_be_found'
                    })}
                </Heading>
                <Box w="full">
                    <Search
                        isSuggestionsEnabled={false}
                        isFocusOnInit={false}
                        containerProps={styles.container}
                        {...styles.search}
                    />
                </Box>
                <Box as="nav" w="full">
                    <UnorderedList
                        styleType="none"
                        gap={32}
                        align={'left'}
                        ml={0}
                        display={'flex'}
                        flexDirection={['column', null, 'row']}
                        justifyContent={'center'}
                    >
                        <ListItem w={['auto', null, '254px']}>
                            <Text variant={'eyeBrowSmall'} textTransform={'uppercase'}>
                                {intl.formatMessage({
                                    defaultMessage: 'Contact us',
                                    id: 'page_not_found.title.contact_us'
                                })}
                            </Text>
                            <UnorderedList styleType="none" spacing={4} ml={0} mt={8}>
                                <ListItem>
                                    <ChakraLink href={`tel:${tel}`}>
                                        {intl.formatMessage({
                                            defaultMessage: 'Call us',
                                            id: 'page_not_found.link.call'
                                        })}
                                    </ChakraLink>
                                </ListItem>
                                <ListItem>
                                    <Link href={CONTACT_US_URL} to={CONTACT_US_URL}>
                                        {intl.formatMessage({
                                            defaultMessage: 'Send us an email',
                                            id: 'page_not_found.link.email'
                                        })}
                                    </Link>
                                </ListItem>
                            </UnorderedList>
                        </ListItem>
                        <ListItem w={['auto', null, '254px']}>
                            <Text variant={'eyeBrowSmall'} textTransform={'uppercase'}>
                                {intl.formatMessage({
                                    defaultMessage: 'How can we help?',
                                    id: 'page_not_found.title.help'
                                })}
                            </Text>
                            <UnorderedList styleType="none" spacing={4} ml={0} mt={8}>
                                <ListItem>
                                    <Link href={FAQ_URL} to={FAQ_URL}>
                                        {intl.formatMessage({
                                            defaultMessage: 'FAQs',
                                            id: 'page_not_found.link.faq'
                                        })}
                                    </Link>
                                </ListItem>
                                <ListItem>
                                    <Link href={'/account'} to={'/account'}>
                                        {intl.formatMessage({
                                            defaultMessage: 'Your account',
                                            id: 'page_not_found.link.account'
                                        })}
                                    </Link>
                                </ListItem>
                            </UnorderedList>
                        </ListItem>
                    </UnorderedList>
                </Box>
            </Flex>
            <Box>
                {pageNotFoundComponents &&
                    pageNotFoundComponents.length > 0 &&
                    pageNotFoundComponents.map((component) => (
                        <PageContext.Provider value={contextValue} key={component.id}>
                            <Component component={component} />
                        </PageContext.Provider>
                    ))}
            </Box>
            <RecommendedProducts
                title={
                    <Heading
                        as="h2"
                        size={['3xl', null, '4xl']}
                        pb={[40, null, 0]}
                        pl={['10px', null, 0]}
                    >
                        {intl.formatMessage({
                            defaultMessage: 'TRENDING PRODUCTS',
                            id: 'page_not_found.recommended_products.title'
                        })}
                    </Heading>
                }
                recommender={EINSTEIN_RECOMMENDERS.PAGE_NOT_FOUND}
                isScroller={false}
                isCarousel={{base: false, md: true, lg: true}}
                mt={[16, null, '52px']}
            />
        </Box>
    )
}

export default PageNotFound
